import React, { useState, useEffect } from 'react';
import { X, DownloadSimple, ArrowLeft } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * PDFViewer - Modal component to view PDFs with close functionality
 * 
 * Props:
 * - isOpen: boolean (controls modal visibility)
 * - onClose: function (callback when closing)
 * - pdfBase64: string (base64 encoded PDF data)
 * - filename: string (name for download)
 * - title: string (modal title)
 */
const PDFViewer = ({ isOpen, onClose, pdfBase64, filename = 'PayGBot_Document.pdf', title = 'PDF Viewer' }) => {
  const [pdfUrl, setPdfUrl] = useState(null);

  useEffect(() => {
    if (pdfBase64) {
      // Convert base64 to blob URL for viewing
      try {
        const byteCharacters = atob(pdfBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        setPdfUrl(url);
      } catch (err) {
        console.error('Error creating PDF URL:', err);
      }
    }

    // Cleanup on unmount
    return () => {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfBase64]);

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleClose = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[60] flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="relative w-full max-w-4xl h-[90vh] bg-slate-900 rounded-2xl shadow-2xl border border-white/10 flex flex-col overflow-hidden"
        >
          {/* Header with Close Button */}
          <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-white/10 px-4 py-3 flex items-center justify-between z-10">
            <div className="flex items-center space-x-3">
              <button
                onClick={handleClose}
                data-testid="pdf-viewer-back-btn"
                className="flex items-center space-x-2 text-slate-400 hover:text-white transition-colors"
              >
                <ArrowLeft size={20} />
                <span className="text-sm font-medium">Back</span>
              </button>
            </div>
            
            <h3 className="text-white font-medium">{title}</h3>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={handleDownload}
                data-testid="pdf-viewer-download-btn"
                className="flex items-center space-x-1.5 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 text-sm font-medium rounded-lg transition-colors"
              >
                <DownloadSimple size={16} />
                <span>Download</span>
              </button>
              
              <button
                onClick={handleClose}
                data-testid="pdf-viewer-close-btn"
                className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                aria-label="Close PDF viewer"
              >
                <X size={18} className="text-white" />
              </button>
            </div>
          </div>

          {/* PDF Content */}
          <div className="flex-1 overflow-hidden bg-slate-800">
            {pdfUrl ? (
              <iframe
                src={`${pdfUrl}#toolbar=0`}
                className="w-full h-full"
                title="PDF Viewer"
                data-testid="pdf-viewer-iframe"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-slate-400">Loading PDF...</p>
              </div>
            )}
          </div>

          {/* Footer with prominent Close */}
          <div className="sticky bottom-0 bg-slate-900/95 backdrop-blur border-t border-white/10 px-4 py-3 flex items-center justify-center">
            <button
              onClick={handleClose}
              data-testid="pdf-viewer-close-footer-btn"
              className="px-6 py-2.5 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors flex items-center space-x-2"
            >
              <X size={18} />
              <span>Close & Return to Offer</span>
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PDFViewer;
